// Chat Sheet Controller
// Controls the bottom sheet chat overlay - matches search drawer pattern
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["backdrop", "sheet", "unreadBadge"]
  static values = { orderToken: String }

  connect() {
    this.isOpen = false

    // Close on escape key
    this.boundHandleEscape = this.handleEscape.bind(this)
    document.addEventListener('keydown', this.boundHandleEscape)
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleEscape)
    this.enableBodyScroll()
  }

  open() {
    this.isOpen = true

    // Show backdrop
    this.backdropTarget.classList.remove('hidden')

    // Trigger animations after a frame
    requestAnimationFrame(() => {
      this.backdropTarget.classList.remove('opacity-0')
      this.backdropTarget.classList.add('opacity-100')
      this.sheetTarget.classList.remove('translate-y-full')
      this.sheetTarget.classList.add('translate-y-0')
    })

    // Prevent body scroll
    this.disableBodyScroll()

    // Focus input after animation
    setTimeout(() => {
      const input = this.sheetTarget.querySelector('input[type="text"]')
      if (input) input.focus()
    }, 350)

    // Clear unread badge when opened
    if (this.hasUnreadBadgeTarget) {
      this.unreadBadgeTarget.classList.add('hidden')
    }

    // Tell the server the user has seen these messages so the global
    // notification badge clears. Fire-and-forget; failures only leave
    // the badge stale (current behavior), no data corruption.
    this.markThreadRead()

    // Scroll to bottom of messages
    const messagesList = this.sheetTarget.querySelector('[data-order-chat-target="messagesList"]')
    if (messagesList) {
      setTimeout(() => {
        messagesList.scrollTop = messagesList.scrollHeight
      }, 100)
    }
  }

  close() {
    this.isOpen = false

    // Fade out backdrop and slide down sheet
    this.backdropTarget.classList.remove('opacity-100')
    this.backdropTarget.classList.add('opacity-0')
    this.sheetTarget.classList.remove('translate-y-0')
    this.sheetTarget.classList.add('translate-y-full')

    // Re-enable body scroll
    this.enableBodyScroll()

    // Hide backdrop after animation
    setTimeout(() => {
      if (!this.isOpen) {
        this.backdropTarget.classList.add('hidden')
      }
    }, 300)
  }

  // Prevent clicks inside the sheet from closing it
  stopPropagation(event) {
    event.stopPropagation()
  }

  handleEscape(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close()
    }
  }

  disableBodyScroll() {
    document.body.style.overflow = 'hidden'
  }

  enableBodyScroll() {
    document.body.style.overflow = ''
  }

  async markThreadRead() {
    if (!this.hasOrderTokenValue) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      await fetch(`/api/orders/${this.orderTokenValue}/messages/mark_read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        }
      })
    } catch (_e) {
      // Silently ignore — stale badge is the existing behavior
    }
  }
}
